import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Spinner, Button, Modal } from "react-bootstrap";
import { FiUsers, FiUserCheck, FiUserPlus, FiFilter } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { API_URL } from "../../../config";
import "./Hris.css";

export default function HRIS() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brokenImageIds, setBrokenImageIds] = useState({});

  const [search, setSearch] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState("all");

  const [visibleCols, setVisibleCols] = useState({});

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [busyDelete, setBusyDelete] = useState(false);

  const columns = useMemo(
    () => [
      { key: "img", label: "Photo" },
      { key: "employeeId", label: "Employee ID" },
      { key: "ename", label: "Name" },
      { key: "dateOfBirth", label: "DOB" },
      { key: "gender", label: "Gender" },
      { key: "phoneNo", label: "Phone" },
      { key: "personal_email", label: "Personal Email" },
      { key: "official_email", label: "Official Email" },
      { key: "department", label: "Department" },
      { key: "service", label: "Service" },
      { key: "fatherName", label: "Father" },
      { key: "motherName", label: "Mother" },
      { key: "address", label: "Address" },
      { key: "emergencyContact", label: "Emergency Contact" },
      { key: "relation", label: "Relation" },
      { key: "bankName", label: "Bank Name" },
      { key: "accountNo", label: "Account No" },
      { key: "ifscCode", label: "IFSC" },
      { key: "accountHolderName", label: "Account Holder" },
      { key: "adarCardNo", label: "Aadhar" },
      { key: "panNo", label: "PAN" },
      { key: "qualification", label: "Qualification" },
      { key: "lastExp", label: "Last Exp" },
      { key: "expWithPWT", label: "Exp With PWT" },
      { key: "interviewDate", label: "Interview Date" },
      { key: "joiningDate", label: "Joining Date" },
      { key: "expectedSalary", label: "Expected Salary" },
      { key: "givenSalary", label: "Given Salary" },
      { key: "workingTime", label: "Working Time" },
      { key: "resumeFile", label: "Resume" },
      { key: "aadhaarFile", label: "Aadhaar Card File" },
      { key: "panFile", label: "PAN Card File" },
      { key: "userType", label: "User Type" },
      { key: "traineeDuration", label: "Trainee Duration" },
      { key: "isActive", label: "Active" },
      { key: "createdAt", label: "Created At" },
    ],
    []
  );

  const stats = useMemo(() => {
    const employees = rows.filter((r) => (r.userType || "").toLowerCase() === "employee").length;
    const trainees = rows.filter((r) => (r.userType || "").toLowerCase() === "trainee").length;
    const active = rows.filter((r) => r.isActive !== false).length;
    return {
      total: rows.length,
      employees,
      trainees,
      active,
    };
  }, [rows]);

  const asText = (value) => (value == null ? "" : String(value).toLowerCase());
  const dateKeys = ["dateOfBirth", "interviewDate", "joiningDate", "createdAt"];

  const formatDateValue = (value) => {
    if (!value) return "N/A";

    // Keep plain YYYY-MM-DD as-is for consistency.
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }

    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return String(value);

    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const getDisplayValue = (row, key) => {
    const value = row?.[key];
    if (value == null || value === "") return "N/A";

    if (dateKeys.includes(key)) {
      return formatDateValue(value);
    }

    if (key === "department") {
      return typeof value === "object" ? value.deptName || "N/A" : value;
    }

    if (key === "service") {
      return typeof value === "object" ? value.serviceName || "N/A" : value;
    }

    if (key === "isActive") {
      return value ? "Yes" : "No";
    }

    return typeof value === "object" ? JSON.stringify(value) : value;
  };

  const resolveImageUrl = (value) => {
    if (!value) return "";
    const raw = String(value).trim();
    if (!raw) return "";
    if (/^(https?:\/\/|data:|blob:)/i.test(raw)) return raw;
    if (raw.startsWith("/")) return `${API_URL}${raw}`;
    return `${API_URL}/${raw}`;
  };

  const renderCell = (row, key) => {
    if (key === "employeeId") {
      const employeeId = row?.employeeId;
      if (!employeeId) return "N/A";
      const isTrainee = (row?.userType || "").toLowerCase() === "trainee";
      const targetPath = isTrainee
        ? `/admin/ViewTrainee/${employeeId}`
        : `/admin/ViewEmployee/${employeeId}`;

      return (
        <button
          type="button"
          className="hris-id-link"
          onClick={() => navigate(targetPath)}
          title={`Open ${employeeId}`}
        >
          {employeeId}
        </button>
      );
    }

    if (key === "resumeFile" || key === "aadhaarFile" || key === "panFile") {
      const fileUrl = row?.[key];
      if (!fileUrl) return "N/A";
      return (
        <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="hris-id-link text-decoration-none">
          View
        </a>
      );
    }

    if (key !== "img") return getDisplayValue(row, key);

    const src = resolveImageUrl(row?.img);
    const isBroken = !!brokenImageIds[row?._id];
    if (!src || isBroken) return <span className="hris-photo-na">N/A</span>;

    return (
      <img
        src={src}
        alt={row?.ename || "Employee"}
        className="hris-photo-thumb"
        onError={() =>
          setBrokenImageIds((prev) => ({
            ...prev,
            [row._id]: true,
          }))
        }
      />
    );
  };

  useEffect(() => {
    const init = {};
    columns.forEach((c, i) => {
      init[c.key] = i < 10;
    });
    setVisibleCols(init);
  }, [columns]);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/getAllEmployees`);
        const list = Array.isArray(res.data) ? res.data : res.data?.employees ?? [];
        setRows(list);
        setFiltered(list);
      } catch {
        setRows([]);
        setFiltered([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  useEffect(() => {
    let temp = [...rows];

    if (userTypeFilter !== "all") {
      if (userTypeFilter === "active") {
        temp = temp.filter((r) => r.isActive !== false);
      } else {
        temp = temp.filter((r) => (r.userType || "").toLowerCase() === userTypeFilter);
      }
    }

    if (search) {
      const s = search.toLowerCase();
      temp = temp.filter(
        (r) =>
          asText(r.ename).includes(s) ||
          asText(r.personal_email).includes(s) ||
          asText(r.employeeId).includes(s) ||
          asText(r.phoneNo).includes(s)
      );
    }

    setFiltered(temp);
  }, [rows, search, userTypeFilter]);

  const toggleColumn = (key) => {
    setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const openDelete = (row) => {
    setToDelete(row);
    setDeleteModalOpen(true);
  };

  const closeDelete = () => {
    setDeleteModalOpen(false);
    setToDelete(null);
  };

  const handleDelete = async () => {
    setBusyDelete(true);
    try {
      await axios.delete(`${API_URL}/api/deleteEmp/${toDelete._id}`);
      setRows((prev) => prev.filter((r) => r._id !== toDelete._id));
      setFiltered((prev) => prev.filter((r) => r._id !== toDelete._id));
      closeDelete();
    } catch {
      alert("Delete failed");
    } finally {
      setBusyDelete(false);
    }
  };

  const exportExcel = () => {
    const exportCols = columns.filter((c) => visibleCols[c.key]);
    const rowsForExport = filtered.map((r) => {
      const rowData = {};
      exportCols.forEach((c) => {
        rowData[c.label] = getDisplayValue(r, c.key);
      });
      return rowData;
    });

    const sheet = XLSX.utils.json_to_sheet(rowsForExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "HRIS");
    XLSX.writeFile(workbook, "HRIS_Export.xlsx");
  };

  if (loading) {
    return (
      <div className="hris-loading-wrap">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="hris-page">
      <div className="hris-hero">
        <div>
          <p className="hris-subtitle">Human Resources</p>
          <h3>HRIS Dashboard</h3>
          <p className="hris-caption">Employee and trainee master records</p>
        </div>
        <div className="hris-hero-actions">
          <button type="button" className="hris-export-btn" onClick={exportExcel}>
            Export Excel
          </button>
          <div className="hris-total-badge">
            <FiFilter />
            <span>{filtered.length} shown</span>
          </div>
        </div>
      </div>

      <div className="hris-stats-grid">
        <article
          className={`hris-stat-card ${userTypeFilter === "all" ? "is-selected-card" : ""}`}
          onClick={() => setUserTypeFilter("all")}
        >
          <div className="hris-stat-icon is-violet">
            <FiUsers />
          </div>
          <div>
            <p>Total Records</p>
            <h4>{stats.total}</h4>
          </div>
        </article>
        <article
          className="hris-stat-card"
          onClick={() => navigate("/admin/employee")}
        >
          <div className="hris-stat-icon is-blue">
            <FiUserCheck />
          </div>
          <div>
            <p>Employees</p>
            <h4>{stats.employees}</h4>
          </div>
        </article>
        <article
          className="hris-stat-card"
          onClick={() => navigate("/admin/trainee")}
        >
          <div className="hris-stat-icon is-amber">
            <FiUserPlus />
          </div>
          <div>
            <p>Trainees</p>
            <h4>{stats.trainees}</h4>
          </div>
        </article>
        <article
          className={`hris-stat-card ${userTypeFilter === "active" ? "is-selected-card" : ""}`}
          onClick={() => setUserTypeFilter("active")}
        >
          <div className="hris-stat-icon is-green">
            <FiUserCheck />
          </div>
          <div>
            <p>Active</p>
            <h4>{stats.active}</h4>
          </div>
        </article>
      </div>

      <div className="hris-panel">
        <div className="hris-controls">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, id, phone"
            className="hris-input"
          />

          <select
            value={userTypeFilter}
            onChange={(e) => setUserTypeFilter(e.target.value)}
            className="hris-select"
          >
            <option value="all">All Types</option>
            <option value="employee">Employee</option>
            <option value="trainee">Trainee</option>
            <option value="active">Active Only</option>
          </select>

          <div className="hris-count">
            <strong>Total:</strong> {filtered.length}
          </div>
        </div>

        <div className="hris-column-wrap">
          {columns.map((c) => (
            <label key={c.key} className="hris-chip">
              <input
                type="checkbox"
                checked={visibleCols[c.key] || false}
                onChange={() => toggleColumn(c.key)}
              />
              <span>{c.label}</span>
            </label>
          ))}
        </div>

        <div className="hris-table-wrap">
          <table className="hris-table">
            <thead>
              <tr>
                {columns.map(
                  (c) =>
                    visibleCols[c.key] && (
                      <th key={c.key}>
                        {c.label}
                      </th>
                    )
                )}
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((r) => (
                <tr key={r._id}>
                  {columns.map(
                    (c) => visibleCols[c.key] && <td key={c.key}>{renderCell(r, c.key)}</td>
                  )}
                  <td>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="me-2"
                      onClick={() => navigate(`/admin/upDateUder/${r.employeeId}`)}
                    >
                      Edit
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => openDelete(r)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal show={deleteModalOpen} onHide={closeDelete}>
        <Modal.Body>Delete {toDelete?.ename}?</Modal.Body>
        <Modal.Footer>
          <Button onClick={closeDelete}>Cancel</Button>
          <Button variant="danger" disabled={busyDelete} onClick={handleDelete}>
            {busyDelete ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
